// useDependencyManager.js
import { useCallback, useRef, useState } from 'react';

export const useDependencyManager = (
  fields,
  formValues,
  setValue,
  updateFormMetadata
) => {
  const dependencyChain = useRef({});
  const [fetchingFields, setFetchingFields] = useState(new Set());

  // Build dependency chain on initialization
  const buildDependencyChain = useCallback(() => {
    const chain = { normal: {}, modal: {} };

    // Process normal form fields
    Object.values(fields).forEach(field => {
      if (field.dependents && Array.isArray(field.dependents)) {
        field.dependents.forEach(dependentId => {
          // Check if it's a modal dependency (contains dot notation)
          if (dependentId.includes('.')) {
            const [modalId, modalFieldId] = dependentId.split('.');
            if (!chain.modal[field.id]) chain.modal[field.id] = [];
            chain.modal[field.id].push({ modalId, fieldId: modalFieldId });
          } else {
            // Normal form dependency
            if (!chain.normal[field.id]) chain.normal[field.id] = [];
            chain.normal[field.id].push(dependentId);
          }
        });
      }
    });

    dependencyChain.current = chain;
  }, [fields]);

  // Fetch dependent data for both normal and modal fields
  const fetchDependentData = useCallback(
    async (fieldId, fieldValue, apiClient) => {
      try {
        const chain = dependencyChain.current;

        // Skip if no value or already fetching
        if (!fieldValue || fetchingFields.has(fieldId)) {
          return;
        }

        // Mark as fetching
        setFetchingFields(prev => new Set(prev).add(fieldId));

        // Handle normal form dependencies
        if (chain.normal[fieldId]) {
          for (const dependentId of chain.normal[fieldId]) {
            await fetchForField(dependentId, fieldValue, null, apiClient);
          }
        }

        // Handle modal dependencies
        if (chain.modal[fieldId]) {
          for (const { modalId, fieldId: modalFieldId } of chain.modal[
            fieldId
          ]) {
            await fetchForField(modalFieldId, fieldValue, modalId, apiClient);
          }
        }
      } catch (error) {
        console.error('Error in fetchDependentData:', error);
      } finally {
        // Remove from fetching set
        setFetchingFields(prev => {
          const newSet = new Set(prev);
          newSet.delete(fieldId);
          return newSet;
        });
      }
    },
    [fetchingFields]
  );

  // Generic fetch function for any field
  const fetchForField = useCallback(
    async (fieldId, parentValue, modalId, apiClient) => {
      try {
        let targetField;

        if (modalId) {
          // Modal field
          const modalField = fields[modalId];
          targetField = modalField?.modal?.fields?.[fieldId];
        } else {
          // Normal field
          targetField = fields[fieldId];
        }

        if (!targetField || !targetField.endpoint) {
          console.log(`⚠️ No endpoint found for field ${fieldId}`);
          return;
        }

        // Skip if the parent value is null/undefined and the endpoint expects it
        if (
          targetField.endpoint.includes('{id}') &&
          (!parentValue || parentValue === 'null')
        ) {
          console.log(
            `⚠️ Skipping fetch for ${fieldId} - parent value is null/undefined`
          );
          return;
        }

        console.log(
          `🔄 Fetching options for ${
            modalId ? 'modal' : 'normal'
          } field ${fieldId} with parent value:`,
          parentValue
        );

        // Build endpoint with dynamic parameter extraction
        let endpoint = targetField.endpoint;

        // Helper function to extract URL parameters
        const extractUrlParameters = endpoint => {
          const currentUrl = window.location.pathname;
          const urlParts = currentUrl
            .split('/')
            .filter(part => part.length > 0);

          console.log(`🔍 Current URL: ${currentUrl}`);
          console.log(`🔍 URL parts:`, urlParts);

          // Extract the last UUID (parent_id) from URL
          const getParentIdFromUrl = () => {
            // Look for UUID pattern in the last parts of URL
            const uuidPattern =
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

            // Check from the end of URL parts
            for (let i = urlParts.length - 1; i >= 0; i--) {
              if (uuidPattern.test(urlParts[i])) {
                console.log(`🎯 Found parent_id in URL: ${urlParts[i]}`);
                return urlParts[i];
              }
            }

            // Fallback: check for any ID-like pattern (longer than 8 chars with hyphens)
            for (let i = urlParts.length - 1; i >= 0; i--) {
              if (urlParts[i].length > 8 && urlParts[i].includes('-')) {
                console.log(
                  `🎯 Found potential parent_id in URL: ${urlParts[i]}`
                );
                return urlParts[i];
              }
            }

            console.log(`⚠️ No parent_id found in URL`);
            return null;
          };

          const getTenantFromUrl = () => {
            // Tenant is typically the first part after domain
            if (urlParts.length > 0) {
              const tenant = urlParts[0];
              console.log(`🏢 Found tenant in URL: ${tenant}`);
              return tenant;
            }
            return null;
          };

          return {
            parent_id: getParentIdFromUrl(),
            tenant: getTenantFromUrl(),
            urlParts
          };
        };

        // Extract URL parameters
        const urlParams = extractUrlParameters(endpoint);

        // Replace {id} with parentValue (from form field)
        if (endpoint.includes('{id}')) {
          endpoint = endpoint.replace('{id}', parentValue);
          console.log(`🔄 Replaced {id} with: ${parentValue}`);
        }

        // Replace {parent_id} with extracted parent_id from URL
        if (endpoint.includes('{parent_id}')) {
          if (urlParams.parent_id) {
            endpoint = endpoint.replace('{parent_id}', urlParams.parent_id);
            console.log(`🔄 Replaced {parent_id} with: ${urlParams.parent_id}`);
          } else {
            console.error(
              `❌ Endpoint requires {parent_id} but none found in URL: ${window.location.pathname}`
            );
            return;
          }
        }

        // Replace {tenant} with extracted tenant from URL
        if (endpoint.includes('{tenant}')) {
          if (urlParams.tenant) {
            endpoint = endpoint.replace('{tenant}', urlParams.tenant);
            console.log(`🔄 Replaced {tenant} with: ${urlParams.tenant}`);
          } else {
            console.error(
              `❌ Endpoint requires {tenant} but none found in URL`
            );
            return;
          }
        }

        // Handle more complex parameter patterns like parent_id={parent_id}
        if (endpoint.includes('parent_id={parent_id}')) {
          if (urlParams.parent_id) {
            endpoint = endpoint.replace(
              'parent_id={parent_id}',
              `parent_id=${urlParams.parent_id}`
            );
            console.log(
              `🔄 Replaced parent_id parameter with: ${urlParams.parent_id}`
            );
          } else {
            console.error(
              `❌ Endpoint requires parent_id parameter but none found in URL`
            );
            return;
          }
        }

        // Add query parameters if needed
        if (targetField.queryParams) {
          const queryParams = new URLSearchParams(targetField.queryParams);

          // Add URL-extracted parameters to query params if they exist
          if (urlParams.parent_id && !queryParams.has('parent_id')) {
            queryParams.set('parent_id', urlParams.parent_id);
          }
          if (urlParams.tenant && !queryParams.has('tenant')) {
            queryParams.set('tenant', urlParams.tenant);
          }

          const queryString = queryParams.toString();
          endpoint += endpoint.includes('?')
            ? `&${queryString}`
            : `?${queryString}`;
        }

        console.log(`📡 Final API endpoint: ${endpoint}`);

        // Make API call
        const response = await apiClient.get(endpoint);
        const options = response?.data?.results || [];

        console.log(`📦 Received ${options.length} options for ${fieldId}`);

        // Update metadata
        if (modalId) {
          // Update modal field options
          console.log(
            `🔧 Updating modal field options for ${modalId}.${fieldId}`
          );
          window.dispatchEvent(
            new CustomEvent('updateModalFieldOptions', {
              detail: { modalId, fieldId, options }
            })
          );
        } else {
          // Update normal field options
          console.log(`🔧 Updating form field options for ${fieldId}`);
          updateFormMetadata(fieldId, options);
        }

        // Enhanced: Don't reset field value if it has existing data (especially in edit mode)
        if (modalId) {
          // For modal fields, we don't reset if there's existing data
          const resetValue = targetField.multiSelect ? [] : '';

          window.dispatchEvent(
            new CustomEvent('resetModalField', {
              detail: {
                modalId,
                fieldId,
                value: resetValue,
                preserveExisting: true
              }
            })
          );
          console.log(
            `🔄 Sent reset signal for modal field ${fieldId} (preserve existing: true)`
          );
        } else {
          // For normal fields - only reset if truly empty
          const currentValue = formValues[fieldId];
          const isEmptyValue =
            !currentValue ||
            currentValue === '' ||
            (Array.isArray(currentValue) && currentValue.length === 0);

          // Check if the field's parent just changed
          const parentField = fields[fieldId]?.dependsOn;
          const parentJustChanged =
            parentField && formValues[parentField] !== parentValue;

          if (isEmptyValue || parentJustChanged) {
            const resetValue = targetField.multiSelect ? [] : '';
            setValue(fieldId, resetValue);
            console.log(`🔄 Reset field ${fieldId} value to:`, resetValue);
          } else {
            console.log(
              `✋ Keeping existing value for ${fieldId}:`,
              currentValue
            );
          }
        }
      } catch (error) {
        console.error(`❌ Error fetching data for field ${fieldId}:`, error);
      }
    },
    [fields, setValue, updateFormMetadata, formValues]
  );

  return {
    buildDependencyChain,
    fetchDependentData,
    dependencyChain: dependencyChain.current
  };
};
